package dev.lingvo.site.components.editor

import dev.lingvo.shared.CommentDto
import dev.lingvo.shared.ReviewStatus
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr

internal const val EDITOR_HEADER_HEIGHT_PX = 52

/**
 * The queue track, and now the grid column that draws it.
 *
 * This held 304 while `EditorWorkbenchQueueStyle` and `EditorLayout` each wrote `304px` out again,
 * so the number the layout arithmetic reasons about and the number the browser lays out by were
 * three independent copies. `EditorStyles` now interpolates this one; `EditorLayout` (another
 * lane's file) still carries its own literal and is the last copy left.
 */
internal const val EDITOR_QUEUE_WIDTH_PX = 304
internal const val EDITOR_QUEUE_HEADER_MAX_HEIGHT_PX = 176

/**
 * How long a layout measurement probe may run before it is abandoned.
 *
 * The probe paints the workspace `visibility: hidden`, so this is the longest the editor may be
 * blank for. 1200ms is far more than any real measurement needs — two animation frames plus a
 * possible side-padding pass — and is deliberately not tuned close: it is a backstop against a
 * probe that will never resolve, not a deadline for one that will.
 */
internal const val EDITOR_MEASUREMENT_TIMEOUT_MS = 1_200L

/**
 * How many times a layout measurement may restart before the layout is settled without it.
 *
 * Generous, because legitimate restarts happen — a viewport resize, opening a context pane, the
 * side-padding pass. This is only here to stop an oscillation between two layouts from hiding the
 * workspace indefinitely, which is a failure the timeout cannot catch: each restart re-keys the
 * watchdog and cancels its delay.
 */
internal const val EDITOR_MEASUREMENT_RESTART_BUDGET = 12L

/**
 * The skip link's target, and the editor's main landmark.
 *
 * Stated once because two places must agree on it — the anchor's `href` and the pane's `id` — and a
 * skip link pointing at an id that is not in the document is a tab stop that silently does nothing,
 * which is worse than having none. Mirrors `AppShell`'s `CONTENT_ID`; the editor does not go
 * through that shell and so had neither the link nor the target.
 */
internal const val EDITOR_CONTENT_ID = "editor-content"

/**
 * A queue row's fixed height, and the number [dev.lingvo.site.components.editor.VirtualizedRows]
 * reasons about: it computes the first and last visible index from scroll position against this
 * one constant, and sizes the spacer divs above and below the rendered window from it too. It must
 * stay one constant that every row actually renders at, or the list drifts out of registration with
 * the browser's own layout — looking correct at the top of a scroll and one row further off with
 * every screen after.
 *
 * 76, not the round 64 it replaced: `KeySourceStyle` is two lines clamped at `TypeScale.BodySmall`
 * (15px). At `line-height: 1.0` the line box was shorter than the font's own ascent+descent, so the
 * two clamped lines physically collided and the row had no slack to fix that without growing. At
 * `line-height: 1.4` the source alone is 42px (2 x 21px); with `Space.SM` padding top and bottom
 * (16), the `Space.XXS` gap before the name row (4), and the name row itself (~14), the total is
 * 76 — raised together with the line-height in the same change, because one without the other still
 * clips the second line.
 */
internal const val EDITOR_ROW_HEIGHT_PX = 76

/**
 * The state rail on a queue row.
 *
 * One constant read from two files on purpose. `EditorQueueStyles` reserves this width transparently
 * on every row and `EditorQueueRows` paints it in the state's colour, so the two have to agree to
 * the pixel — when they did not, a row moved sideways at the moment it gained a state.
 */
internal const val STATE_RAIL_PX = 4
internal const val EDITOR_CONTENT_MAX_WIDTH_PX = 820
internal const val EDITOR_CONTENT_HORIZONTAL_PADDING_PX = 32
internal const val EDITOR_ACTION_BAR_HEIGHT_PX = 52

/**
 * The utility track, in one place instead of five.
 *
 * 320 floor, 400 ceiling, 31.25vw between. The 400 is a ceiling reached only at 1280 and wider, so
 * the constant that used to sit here — `EDITOR_ASSIST_WIDTH_PX = 400`, referenced by nothing —
 * described a fixed column the editor has never had, and reading it was the fastest way to believe
 * a wrong number.
 *
 * The clamp itself was written out at five property sites across two files, so the workspace's grid
 * column and the sidecar's own flex basis could drift apart and leave the panel overhanging the
 * grid by the difference. Interpolated whole and never assembled from parts at the call site: a
 * hand-built `calc()` string once shipped as literal text and the browser dropped it silently.
 */
internal const val EDITOR_UTILITY_TRACK = "clamp(320px, 31.25vw, 400px)"

/**
 * The wide end of [EDITOR_UTILITY_TRACK], and the figure the trio threshold budgets for.
 *
 * The narrow end (320) is the wrong number to reason with: the track is
 * `clamp(320px, 31.25vw, 400px)`, and 31.25vw reaches 400 at 1280 — every width where three panes
 * could plausibly fit is already paying the maximum. Budgeting the minimum produced a threshold of
 * 1248 that promised a trio the writing column could not actually afford at 1280.
 */
internal const val EDITOR_UTILITY_MAX_WIDTH_PX = 400

/**
 * The width at which the queue, the editor and a context pane can all be on screen at once.
 *
 * `docs/DESIGN.md` asks for three panes and the product built two, alternating: opening Assist or
 * Activity **replaced** the key queue rather than joining it. That is the wrong trade in the one
 * place it matters most — a translation-memory hit is judged against the strings around it, and the
 * strings around it were exactly what opening the suggestion removed.
 *
 * It is a threshold rather than a promise because three fixed panes do not fit everywhere: the
 * document's own 380 + 340 leaves 596px of writing column on a 1280 laptop, barely over
 * [EDITOR_MIN_WRITING_WIDTH_PX], and 276px on a 1024 tablet. So the arithmetic is stated instead —
 * queue + narrowest utility + the minimum writing measure and its padding — and below it the queue
 * yields, which is the old behaviour kept for the widths that genuinely need it.
 */
internal const val EDITOR_TRIO_THRESHOLD_PX = EDITOR_QUEUE_WIDTH_PX +
    EDITOR_UTILITY_MAX_WIDTH_PX +
    EDITOR_MIN_WRITING_WIDTH_PX +
    EDITOR_PREFERRED_SIDE_PADDING_PX * 2

internal enum class EditorMode { TRANSLATION, REVIEW }

/**
 * One of the two ways a key is finished.
 *
 * **There is no short label any more.** There used to be — "Approve" for "Approve and next" — for
 * screens the full one did not fit on, because at 320px the two full labels needed more width than
 * the 52px action dock had and wrapped inside a 40px button. Below the narrow breakpoint the pair
 * are now `LIconAction`s, so the dock holds two glyphs and the *full* name moves into the tooltip
 * and the accessible name: the reader with the least room is no longer the one told the least.
 */
internal data class EditorPrimaryAction(val label: String, val approve: Boolean)

internal enum class EditorFilterId { STATE, FILE, TAG, REVIEW, STALE }

internal data class EditorAppliedFilter(val id: EditorFilterId, val label: String)

internal data class EditorTextInsertion(val value: String, val caret: Int)

internal fun EditorQuery.without(filter: EditorFilterId): EditorQuery = when (filter) {
    EditorFilterId.STATE -> copy(state = EditorStateFilter.ALL)
    EditorFilterId.FILE -> copy(fileId = "")
    EditorFilterId.TAG -> copy(tag = "")
    EditorFilterId.REVIEW -> copy(review = null)
    EditorFilterId.STALE -> copy(stale = false)
}

internal fun editorMode(review: ReviewStatus?): EditorMode =
    if (review == ReviewStatus.PENDING) EditorMode.REVIEW else EditorMode.TRANSLATION

/**
 * Every action the bar offers, in the order it draws them.
 *
 * Translation mode used to offer only "Save and next"; an Approve control existed only in
 * [EditorMode.REVIEW], reachable only by a work-queue link with no control anywhere else for it —
 * so the only route to an on-screen Approve was a deep link the editor could not construct, while
 * approving is the terminal action on every key for someone who machine-translates and then reviews
 * their own output. Both actions are offered in both modes now.
 *
 * The first element is what `EditorActionBar` renders as *the* primary action — the one styled
 * `LButtonVariant.PRIMARY` and carrying `data-editor-primary`, both read by existing DOM contracts
 * (`TranslationPaneSurfaceTest`, `EditorControllerGeometryTest`) that predate this function and must
 * keep seeing exactly one such element. That is also why the two modes order their list differently
 * rather than sharing one: `.first()` is this function's whole replacement for the single value
 * `primaryEditorAction` used to return, and each mode's own primary action has to stay first.
 */
/**
 * The action bar's two buttons, primary first.
 *
 * **No longer keyed on [EditorMode], and that is the finding rather than a simplification.** The two
 * modes ranked these differently because two people used them: the invited translator typed and
 * saved, the reviewer approved. `PRODUCT.md` was amended on 2026-08-26 to say those are one person —
 * the repository owner, who machine-translates and then reviews their own output — so approving is
 * the action that ends the loop on every key, in every mode. Two rankings for one audience is a
 * distinction the product no longer draws.
 *
 * [editorMode] itself stays: it still decides other things, and a `PENDING` review is still a real
 * state. It just no longer changes which button is loudest.
 *
 * **Approving a blank translation is already impossible and needs no guard here.** `inlineQa`
 * raises an ERROR for an empty singular ("Translation is empty.", `EditorModels.kt:195`) and for
 * every missing plural category ("… is required for this locale.", `:164`), and the action bar
 * disables Approve whenever any ERROR is present. A `value.isBlank()` check was added here on the
 * assumption that QA had nothing to say about an empty string; it was unreachable, and the
 * assumption was wrong. [approveReady] is that same QA answer, passed in — the empty draft is
 * inside it, which is why there is no second blankness argument.
 *
 * **Which one is loudest *is* keyed on state, and only on state.** The list is ordered, and
 * `EditorActionBar` makes element 0 the `PRIMARY` carrying `data-editor-primary`. With Approve
 * unconditionally first, the queue's most common row — an untranslated string, where `inlineQa`
 * raises "Translation is empty." — drew the accent button `aria-disabled` and the one control that
 * worked, "Save and next", in `SECONDARY` beside it. Ordering by [approveReady] keeps the
 * cardinality at exactly one either way and makes the loud button the one that does something.
 */
internal fun editorActions(approveReady: Boolean): List<EditorPrimaryAction> {
    val approve = EditorPrimaryAction(
        tr("editor.editor_presentation.approve_and_next", "Approve and next"),
        approve = true,
    )
    val save = EditorPrimaryAction(
        tr("editor.editor_presentation.save_and_next", "Save and next"),
        approve = false,
    )
    return if (approveReady) listOf(approve, save) else listOf(save, approve)
}


internal fun remainingTranslations(total: Int, translated: Int, approved: Int): Int =
    (total - translated - approved).coerceAtLeast(0)

internal fun assistAttentionCount(hasProposal: Boolean, comments: List<CommentDto>?): Int =
    (if (hasProposal) 1 else 0) + comments.orEmpty().count { !it.resolved }

internal fun appliedEditorFilters(query: EditorQuery, filePath: String?): List<EditorAppliedFilter> =
    buildList {
        if (query.state != EditorStateFilter.ALL) {
            add(EditorAppliedFilter(EditorFilterId.STATE, query.state.label))
        }
        if (query.fileId.isNotBlank()) {
            add(EditorAppliedFilter(EditorFilterId.FILE, filePath ?: query.fileId))
        }
        if (query.tag.isNotBlank()) {
            add(EditorAppliedFilter(EditorFilterId.TAG, query.tag))
        }
        when (query.review) {
            ReviewStatus.PENDING -> add(EditorAppliedFilter(EditorFilterId.REVIEW, "Awaiting review"))
            ReviewStatus.CHANGES_REQUESTED -> {
                add(EditorAppliedFilter(EditorFilterId.REVIEW, "Changes requested"))
            }
            null, ReviewStatus.NONE -> Unit
        }
        if (query.stale) {
            add(EditorAppliedFilter(EditorFilterId.STALE, "Source changed"))
        }
    }

internal fun estimatedTargetWidth(viewportWidth: Int, queueVisible: Boolean = true): Int {
    val queue = if (queueVisible) EDITOR_QUEUE_WIDTH_PX else 0
    val available = viewportWidth - queue - EDITOR_CONTENT_HORIZONTAL_PADDING_PX * 2
    return available.coerceAtLeast(0).coerceAtMost(EDITOR_CONTENT_MAX_WIDTH_PX)
}

internal fun estimatedVisibleQueueRows(viewportHeight: Int): Int =
    ((viewportHeight - EDITOR_HEADER_HEIGHT_PX - EDITOR_QUEUE_HEADER_MAX_HEIGHT_PX) /
        EDITOR_ROW_HEIGHT_PX).coerceAtLeast(0)

internal fun insertText(
    value: String,
    selectionStart: Int,
    selectionEnd: Int,
    insertion: String,
): EditorTextInsertion {
    val start = selectionStart.coerceIn(0, value.length)
    val end = selectionEnd.coerceIn(start, value.length)
    val updated = value.substring(0, start) + insertion + value.substring(end)
    return EditorTextInsertion(updated, start + insertion.length)
}
